import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, CheckCircle2, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAppState } from "@/context/StateContext";
import { fmt } from "@/lib/format";

const OPTIONS = [
  { id: "everything", label: "Everything looks right" },
  { id: "extra", label: "Extra expense" },
  { id: "bill", label: "Bill changed" },
  { id: "everyday", label: "Adjust everyday spending" },
  { id: "income", label: "Additional income" },
];

const emptyAdj = {
  additional_income: "",
  extra_expense: "",
  extra_expense_name: "",
  extra_expense_persist: false,
  extra_expense_frequency: "monthly",
  extra_expense_custom_days: "",
  extra_expense_due_day: "",
  extra_expense_next_due_date: "",
  living_override: "",
  living_persist: false,
  bill_id: "",
  bill_amount: "",
  bill_persist: false,
};
const fieldCls = "mt-1 w-full rounded-lg pe-surface px-3 py-2.5 text-base text-[#171717] outline-none focus:ring-2 focus:ring-[#2F6B57]";

const FREQ_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "every_two_weeks", label: "Every 2 weeks" },
  { value: "twice_monthly", label: "Twice monthly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
  { value: "custom", label: "Custom" },
];

function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          data-testid={`${testid}-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`flex-1 rounded-lg border px-3 py-2 text-[13px] font-medium leading-snug transition-colors ${
            value === o.value
              ? "border-[#2F6B57] bg-[#DDE7DF] text-[#164C3B]"
              : "border-[#E5E0D9] bg-white text-[#70584A] hover:bg-[#F5F2EC]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function MoneyLandedModal({ open, onOpenChange }) {
  const { state, calculate } = useAppState();
  const navigate = useNavigate();
  const paycheck = state?.foundation?.income?.take_home_paycheck ?? null;
  const essentials = state?.foundation?.essentials ?? [];

  const [amount, setAmount] = useState("");
  const [editing, setEditing] = useState(false);
  const [showChanged, setShowChanged] = useState(false);
  const [mode, setMode] = useState(null);
  const [adj, setAdj] = useState(emptyAdj);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(paycheck != null ? String(paycheck) : "");
      setEditing(paycheck == null);
      setShowChanged(false);
      setMode(null);
      setAdj(emptyAdj);
    }
  }, [open, paycheck]);

  const buildAdjustments = () => {
    const a = {};
    if (parseFloat(adj.additional_income) > 0) a.additional_income = parseFloat(adj.additional_income);
    if (parseFloat(adj.extra_expense) > 0) {
      a.extra_expense = parseFloat(adj.extra_expense);
      a.extra_expense_name = adj.extra_expense_name || "One-time expense";
      if (adj.extra_expense_persist) {
        a.extra_expense_persist = true;
        a.extra_expense_frequency = adj.extra_expense_frequency || "monthly";
        if (adj.extra_expense_frequency === "custom" && parseFloat(adj.extra_expense_custom_days) > 0)
          a.extra_expense_custom_days = parseInt(adj.extra_expense_custom_days, 10);
        if (adj.extra_expense_frequency === "monthly" && parseInt(adj.extra_expense_due_day, 10) > 0)
          a.extra_expense_due_day = parseInt(adj.extra_expense_due_day, 10);
        if (adj.extra_expense_frequency !== "monthly" && adj.extra_expense_next_due_date)
          a.extra_expense_next_due_date = adj.extra_expense_next_due_date;
      }
    }
    if (adj.living_override !== "" && !isNaN(parseFloat(adj.living_override))) {
      a.living_override = parseFloat(adj.living_override);
      if (adj.living_persist) a.living_persist = true;
    }
    if (adj.bill_id && adj.bill_amount !== "") {
      a.bill_overrides = [{ essential_id: adj.bill_id, amount: parseFloat(adj.bill_amount) || 0 }];
      if (adj.bill_persist) a.bill_persist = true;
    }
    return Object.keys(a).length ? a : null;
  };

  const submit = async (value) => {
    const val = parseFloat(value != null ? value : amount);
    if (isNaN(val) || val < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await calculate(val, { income_date: today, adjustments: buildAdjustments() });
      onOpenChange(false);
      navigate("/sprint");
    } catch (e) {
      toast.error("Could not calculate your Sprint. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const num = (v) => v.replace(/[^0-9.]/g, "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md gap-0 overflow-y-auto rounded-2xl border-[#E5E0D9] bg-[#FAF8F4] p-0" data-testid="landed-money-modal">
        <div className="p-6 sm:p-7">
          <DialogHeader className="space-y-2 text-left">
            <span className="flex min-h-11 w-11 items-center justify-center rounded-xl bg-[#DDE7DF] text-[#164C3B]">
              <Wallet className="h-5 w-5" />
            </span>
            <DialogTitle className="pt-1 font-serif text-2xl font-normal text-[#171717]">How much landed?</DialogTitle>
            <DialogDescription className="text-[15px] text-[#525252]">
              We'll use the financial foundation you've already set up.
            </DialogDescription>
          </DialogHeader>

          {paycheck != null && !editing ? (
            <div className="mt-6 rounded-xl pe-surface p-4" data-testid="paycheck-confirm">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70584A]">Your usual paycheck</p>
              <p className="mt-1 font-serif text-3xl text-[#164C3B]">{fmt(paycheck)}</p>
              <p className="mt-3 text-sm text-[#525252]">Is this what you received?</p>
              <div className="mt-2.5 flex flex-col gap-2 sm:flex-row">
                <Button data-testid="landed-confirm-yes" onClick={() => submit(paycheck)} disabled={loading} className="min-h-11 flex-1 rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]">
                  {loading ? "Calculating…" : "Yes, that's right"}
                </Button>
                <Button variant="outline" data-testid="landed-enter-different" onClick={() => { setEditing(true); setAmount(""); }} className="min-h-11 flex-1 rounded-xl border-[#E5E0D9] bg-white text-base font-medium text-[#70584A] hover:bg-[#F5F2EC]">
                  Enter a different amount
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-6">
              <label htmlFor="landed-input" className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70584A]">Amount that came in</label>
              <div className="mt-2 flex items-center rounded-xl pe-surface px-4 focus-within:ring-2 focus-within:ring-[#2F6B57]">
                <span className="font-serif text-3xl text-[#164C3B]">$</span>
                <input id="landed-input" data-testid="landed-money-amount-input" inputMode="decimal" type="text" value={amount} autoFocus
                  onChange={(e) => setAmount(num(e.target.value))} onKeyDown={(e) => e.key === "Enter" && submit()}
                  placeholder="0" className="w-full bg-transparent py-3 pl-1 font-serif text-3xl text-[#171717] outline-none placeholder:text-[#C9C2B8]" />
              </div>
            </div>
          )}

          <div className="mt-5 flex items-start gap-2 rounded-xl bg-[#DDE7DF] p-3.5 text-[13px] leading-relaxed text-[#164C3B]" data-testid="landed-reassurance">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#2F6B57]" />
            <span><strong>We've got the rest.</strong> We'll cover what's due before your next income, then see what's left. Your money never moves. You make every payment yourself.</span>
          </div>

          <button type="button" data-testid="something-changed-button" onClick={() => setShowChanged((v) => !v)} className="mt-3 text-sm font-medium text-[#2F6B57] hover:underline">
            Anything different before your next income? →
          </button>

          {showChanged && (
            <div className="mt-2 space-y-2" data-testid="something-changed-options">
              <div className="divide-y divide-[#E5E0D9] overflow-hidden rounded-xl pe-surface">
                {OPTIONS.map((o, i) => (
                  <button key={o.id} type="button" data-testid={`change-option-${i}`}
                    onClick={() => { if (o.id === "everything") { setAdj(emptyAdj); setMode(null); toast("Great. Nothing to change."); } else { setMode(o.id); } }}
                    className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm transition-colors hover:bg-[#F5F2EC] ${mode === o.id ? "bg-[#F5F2EC] text-[#164C3B]" : "text-[#171717]"}`}>
                    {o.label}
                    <ChevronRight className="h-4 w-4 text-[#A89A88]" />
                  </button>
                ))}
              </div>

              {mode === "income" && (
                <div className="rounded-xl pe-surface p-3">
                  <label className="text-xs font-medium text-[#70584A]">How much extra income came in?</label>
                  <input data-testid="adj-additional-income" inputMode="decimal" value={adj.additional_income}
                    onChange={(e) => setAdj({ ...adj, additional_income: num(e.target.value) })} placeholder="$0" className={fieldCls} />
                  <p className="mt-2 text-[12px] leading-relaxed text-[#8A7C6B]">We'll count this with the money that just landed. Your usual income stays the same. Update it in your Foundation if it's changed for good.</p>
                </div>
              )}
              {mode === "extra" && (
                <div className="rounded-xl pe-surface p-3 space-y-2">
                  <input data-testid="adj-extra-name" value={adj.extra_expense_name} onChange={(e) => setAdj({ ...adj, extra_expense_name: e.target.value })} placeholder="What is it? (e.g. Car repair)" className={fieldCls} />
                  <input data-testid="adj-extra-expense" inputMode="decimal" value={adj.extra_expense} onChange={(e) => setAdj({ ...adj, extra_expense: num(e.target.value) })} placeholder="$ Amount to set aside" className={fieldCls} />
                  <p className="pt-1 text-xs font-medium text-[#70584A]">Is this a one-time expense or ongoing?</p>
                  <Segmented
                    testid="adj-extra-persist"
                    value={adj.extra_expense_persist ? "perm" : "temp"}
                    onChange={(v) => setAdj({ ...adj, extra_expense_persist: v === "perm" })}
                    options={[{ value: "temp", label: "One-time" }, { value: "perm", label: "Ongoing: save to my Foundation" }]}
                  />
                  {adj.extra_expense_persist && (
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs font-medium text-[#70584A]">How often does it come up?</label>
                        <select data-testid="adj-extra-frequency" value={adj.extra_expense_frequency} onChange={(e) => setAdj({ ...adj, extra_expense_frequency: e.target.value })} className={fieldCls}>
                          {FREQ_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                      </div>
                      {adj.extra_expense_frequency === "monthly" && (
                        <input data-testid="adj-extra-due-day" inputMode="numeric" value={adj.extra_expense_due_day} onChange={(e) => setAdj({ ...adj, extra_expense_due_day: num(e.target.value).slice(0, 2) })} placeholder="Due day of month (1–31)" className={fieldCls} />
                      )}
                      {adj.extra_expense_frequency === "custom" && (
                        <input data-testid="adj-extra-custom-days" inputMode="numeric" value={adj.extra_expense_custom_days} onChange={(e) => setAdj({ ...adj, extra_expense_custom_days: num(e.target.value) })} placeholder="Every how many days?" className={fieldCls} />
                      )}
                      {adj.extra_expense_frequency !== "monthly" && adj.extra_expense_frequency !== "custom" && (
                        <div>
                          <label className="text-xs font-medium text-[#70584A]">When is the next one due?</label>
                          <input data-testid="adj-extra-next-due" type="date" value={adj.extra_expense_next_due_date} onChange={(e) => setAdj({ ...adj, extra_expense_next_due_date: e.target.value })} className={fieldCls} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {mode === "everyday" && (
                <div className="rounded-xl pe-surface p-3 space-y-2">
                  <label className="text-xs font-medium text-[#70584A]">New everyday spending amount for this stretch</label>
                  <input data-testid="adj-living" inputMode="decimal" value={adj.living_override} onChange={(e) => setAdj({ ...adj, living_override: num(e.target.value) })} placeholder="$0" className={fieldCls} />
                  <Segmented
                    testid="adj-living-persist"
                    value={adj.living_persist ? "perm" : "temp"}
                    onChange={(v) => setAdj({ ...adj, living_persist: v === "perm" })}
                    options={[{ value: "temp", label: "Just this stretch" }, { value: "perm", label: "Save as my new everyday baseline" }]}
                  />
                </div>
              )}
              {mode === "bill" && (
                <div className="rounded-xl pe-surface p-3 space-y-2">
                  <select data-testid="adj-bill-select" value={adj.bill_id} onChange={(e) => setAdj({ ...adj, bill_id: e.target.value })} className={fieldCls}>
                    <option value="">Which bill changed?</option>
                    {essentials.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                  <input data-testid="adj-bill-amount" inputMode="decimal" value={adj.bill_amount} onChange={(e) => setAdj({ ...adj, bill_amount: num(e.target.value) })} placeholder="$ New amount" className={fieldCls} />
                  <Segmented
                    testid="adj-bill-persist"
                    value={adj.bill_persist ? "perm" : "temp"}
                    onChange={(v) => setAdj({ ...adj, bill_persist: v === "perm" })}
                    options={[{ value: "temp", label: "Just this time" }, { value: "perm", label: "Update this bill going forward" }]}
                  />
                </div>
              )}
            </div>
          )}

          {(editing || paycheck == null) && (
            <Button data-testid="calculate-sprint-button" onClick={() => submit()} disabled={loading} className="mt-5 min-h-12 w-full rounded-xl bg-[#2F6B57] text-base font-semibold text-white hover:bg-[#255643]">
              {loading ? "Calculating…" : "Calculate My Sprint"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
